import ky from 'ky';
import { toast } from 'sonner';
import { parseApiErrorHook } from '@/lib/api/error';
import { getUserAccessToken } from '@/lib/auth/store';
import { redirectToLogin, refreshTokenNow } from '@/lib/auth/token-refresh';
import { config } from '@/lib/env';

const REQUEST_TIMEOUT_MS = 30_000;
const RATE_LIMIT_TOAST_ID = 'rate-limited';

// bopApi is created with retry: 0 deliberately (see below), and ky's retry-limit
// check applies uniformly to forced and automatic retries — so `ky.retry()` would
// silently no-op here. Manually reissuing via a plain fetch() sidesteps that;
// ky clones the request specifically so hooks can safely reuse it, bodies included.
const reissueWithFreshToken = (request: Request): Promise<Response> => {
  const token = getUserAccessToken();
  const headers = new Headers(request.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  return fetch(new Request(request, { headers }));
};

// 401 handler: one silent refresh + retry, then a hard redirect to Okta if the
// refresh itself fails, or if the retried request is unauthorized again (never
// a second refresh attempt — avoids a refresh/401 loop).
const handleUnauthorized = async (request: Request): Promise<Response | undefined> => {
  const refreshed = await refreshTokenNow();
  if (!refreshed) {
    redirectToLogin();
    return undefined;
  }

  const retryResponse = await reissueWithFreshToken(request);
  if (retryResponse.status === 401) {
    redirectToLogin();
    return undefined;
  }
  return retryResponse;
};

export const bopApi = ky.create({
  prefixUrl: config.BOP_API_BASE_URL,
  // Retries are TanStack Query's job — never double up at the HTTP layer
  retry: 0,
  timeout: REQUEST_TIMEOUT_MS,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getUserAccessToken();
        if (token) request.headers.set('authorization', `Bearer ${token}`);
      },
    ],
    afterResponse: [
      (request, _options, response) => {
        if (response.status === 429) {
          toast.error('Rate limited — retry shortly.', { id: RATE_LIMIT_TOAST_ID });
        }
        if (response.status !== 401) return undefined;
        return handleUnauthorized(request);
      },
    ],
    beforeError: [parseApiErrorHook],
  },
});

// Unauthenticated sidecar services the portal calls directly (both need CORS
// for the SPA origin).
export const fastApi = ky.create({
  prefixUrl: config.FAST_API_BASE_URL,
  retry: 0,
  timeout: REQUEST_TIMEOUT_MS,
  hooks: { beforeError: [parseApiErrorHook] },
});

export const cryptoInfo = ky.create({
  prefixUrl: config.CRYPTO_INFO_BASE_URL,
  retry: 0,
  timeout: REQUEST_TIMEOUT_MS,
  hooks: { beforeError: [parseApiErrorHook] },
});

// Non-prod market-maker console sidecar. No auth of any kind — the service is only
// deployed in non-production environments, and routing these calls through bopApi
// would leak the bearer token (and its 401 hook would sign the operator out).
// Needs CORS for the SPA origin. ⚠️ ky prefixUrl: request paths must NOT start with '/'.
export const marketMaker = ky.create({
  prefixUrl: config.MARKET_MAKER_BASE_URL ?? '', // page is gated; never called when unset
  retry: 0, // retries are TanStack Query's job
  timeout: REQUEST_TIMEOUT_MS,
  hooks: { beforeError: [parseApiErrorHook] },
});
